import { useEffect } from 'react';
import axios from 'axios';

import { getItem, storeItem } from '../helpers/store';
import { useSignOut } from './useSignOut';

const WHITELISTED_URLS = [/^\/login$/, /^\/shared_lists\/[^/]+\/contacts$/];

// Shared single-flight refresh state
let refreshPromise = null;

/**
 * Performs a single in-flight token refresh. Subsequent callers await the same promise.
 * Resolves when refresh succeeds (and token is stored) or throws on failure.
 */
const refreshAuthToken = async (signOut) => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/refresh`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.ok) {
      const token = response.headers?.get('Authorization');
      if (token) {
        storeItem('authToken', token);
      }
      return token;
    }

    if (response.status === 401) {
      // Session is no longer valid
      signOut();
    }

    throw new Error('Failed to refresh auth token');
  })().finally(() => {
    // Allow next refresh attempts
    refreshPromise = null;
  });

  return refreshPromise;
};

/**
 * Check if a URL should be excluded from sign out behavior
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL should be whitelisted
 */
const isUrlWhitelisted = (url = '') => {
  let pathname = url;

  if (url.includes('?')) {
    pathname = url.split('?')[0];
  }

  return WHITELISTED_URLS.some((pattern) => pattern.test(pathname));
};

export const useAxiosInterceptors = () => {
  const { signOut } = useSignOut();

  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const accessToken = getItem('authToken');

      if (accessToken) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = accessToken;
      }

      return config;
    });

    const resId = axios.interceptors.response.use(
      (response) => {
        const { authorization } = response?.headers;

        if (authorization) {
          storeItem('authToken', authorization);
        }

        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        const status = error?.response?.status;

        if (status === 401 && !originalRequest.retry) {
          if (isUrlWhitelisted(originalRequest.url || '')) {
            return Promise.reject(error);
          }

          originalRequest.retry = true;

          try {
            await refreshAuthToken(signOut);
            // Retry the original request after refresh. It will pick up the new token via request interceptor.
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh failed; propagate original error
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};
